import React, { createContext, forwardRef, useContext, useEffect, useImperativeHandle } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { ChatInputBox, ChatInputBoxOnSend, FileUpload } from "../../../components/shared/chat/chat-input-box";
import { TekkrIcon } from "../../../components/shared/tekkr/tekkr-logo";
import { useCurrentOrgUser } from "../../../auth/org-provider";
import AnimateHeight from "react-animate-height";
import { useProject } from "../../../data/project";
import { mapUploadsToAttachments, useChatCredentials, useSdkChatWrapper } from "../../../data/hooks/use-chat";
import { cn } from "../../../lib/utils";
import { FocusIcon, XIcon } from "lucide-react";
import { ErrorBox } from "../../../components/shared/chat/error-box";
import { useChatError } from "../../../data/hooks/use-chat-error";
import { useCreateChatMutation } from "../../../data/mutations/create-chat";
import { useCreateAgentChatMutation } from "../../../data/mutations/create-agent-chat";
import { useListProjectChatsQuery } from "../../../data/queries/list-chats";
import { useListAgentsQuery } from "../../../data/queries/list-agents";
import { ProjectChatUiContext } from "tekkr-common/dist/model/project/chat";
import { ChatToolName } from "tekkr-common/dist/model/chats/tools";
import { useScrollToBottomWhileStreaming } from "../../../components/hooks/scroll-to-bottom-while-streaming";
import { MessagesThread } from "./messages-thread";
import { useCreateGoogleDriveCompareMutation } from "../../../data/mutations/create-google-drive-compare";
import { useSelectGoogleDriveCompareMutation } from "../../../data/mutations/select-google-drive-compare";
import { GoogleDriveCompareBlock, GoogleDriveCompareStatusBlock } from "./google-drive-compare-block";
import { useFlags } from "flagsmith/react";
import { FileSuggestion, SuggestedContextPanel } from "./tools/google-drive-context";
import { config } from "../../../lib/config";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../../../components/ui/tooltip";
import { getIconComponent } from "../../../components/icon-picker";
import { UIMessage } from "ai";
import { ReactNode } from "react";
import { GoogleDriveChatBanner } from "../../../components/shared/google-drive/google-drive-chat-banner";

const TOOLS_INVALIDATING_TASKS: ChatToolName[] = ["createProjectTask", "updateProjectTasks", "deleteProjectTasks"];
const TOOLS_INVALIDATING_PROJECT: ChatToolName[] = ["applyPlanChanges"];

// Context for chat actions that tool components can use
interface ChatActionsContextType {
    sendMessage: (message: string) => void;
    onMarkAsSent?: (content: string) => void;
}

const ChatActionsContext = createContext<ChatActionsContextType | null>(null);

export function useChatActions() {
    const ctx = useContext(ChatActionsContext);
    if (!ctx) throw new Error("useChatActions must be used within ChatActionsContext");
    return ctx;
}

interface ChatSubpageProps {
    chatId: string | null; // null is for empty state to start new chats
    onChatCreated: (chatId: string) => void;
    className?: string;
    uiContext: ProjectChatUiContext;
    agentId?: string | null;
    onClearAgent?: () => void;
    renderFooterActions?: (messages: UIMessage[]) => ReactNode;
    onMarkAsSent?: (content: string) => void;
}

export interface ChatSubpageApi {
    startChatWithMessage: ChatInputBoxOnSend;
}

interface InitialMessage {
    content: string;
    uploads: FileUpload[];
    sent: boolean;
}

export const ChatSubpage = forwardRef<ChatSubpageApi, ChatSubpageProps>((props, ref) => {
    const [initialMessage, setInitialMessage] = React.useState<InitialMessage | null>(null);
    const [error, setError] = React.useState<string | undefined>(undefined);
    const createChatMutation = useCreateChatMutation(props.uiContext, {
        onSuccess: (data) => {
            props.onChatCreated(data.chatId);
        },
    });

    // Only create the agent chat mutation hook if we have an agentId
    const createAgentChatMutation = useCreateAgentChatMutation(props.agentId || "", props.uiContext, {
        onSuccess: (data) => {
            props.onChatCreated(data.chatId);
        },
    });

    const startChat: ChatInputBoxOnSend = (msg, uploads) => {
        setInitialMessage({ content: msg, sent: false, uploads });
        setError(undefined);
        const mutation = props.agentId ? createAgentChatMutation : createChatMutation;
        mutation.mutateAsync().catch(() => {
            setError("Error starting new chat session. Please try again later.");
        });
    };

    useImperativeHandle(ref, () => ({
        startChatWithMessage: startChat,
    }));

    const credentialHeaders = useChatCredentials();

    if (!props.chatId) {
        return (
            <NewChatContent
                onSend={startChat}
                uiContext={props.uiContext}
                agentId={props.agentId}
                onClearAgent={props.onClearAgent}
                error={error}
            />
        );
    } else if (credentialHeaders) {
        return (
            <ChatContent
                key={props.chatId}
                chatId={props.chatId}
                initialMessage={initialMessage ?? undefined}
                credentialsHeaders={credentialHeaders}
                renderFooterActions={props.renderFooterActions}
                uiContext={props.uiContext}
                onMarkAsSent={props.onMarkAsSent}
            />
        );
    }

    return null;
});
ChatSubpage.displayName = "ChatSubpage";

function NewChatContent(props: {
    onSend: ChatInputBoxOnSend;
    uiContext: ProjectChatUiContext;
    agentId?: string | null;
    onClearAgent?: () => void;
    error?: string;
}) {
    return (
        <div className={cn("flex w-full grow flex-col items-center p-4 pb-24")}>
            <div
                className={cn(
                    "h-full w-full flex-col items-center justify-center gap-6 transition-all duration-700",
                    "flex max-w-2xl items-stretch"
                )}
            >
                <EmptyChatHeader
                    visible={true}
                    uiContext={props.uiContext}
                    agentId={props.agentId}
                    onClearAgent={props.onClearAgent}
                />
                <GoogleDriveChatBanner />
                <ChatInputBox
                    className={"relative duration-500 animate-in fade-in-55 slide-in-from-bottom-1.5"}
                    onSend={props.onSend}
                />
                {props.error && <ErrorBox error={props.error} />}
            </div>
        </div>
    );
}

type CompareState =
    | null
    | {
          status: "generating";
      }
    | {
          status: "ready";
          experimentId: string;
          optionA: string;
          optionB: string;
      };

type ChatContentProps = {
    chatId: string;
    initialMessage?: InitialMessage;
    credentialsHeaders: Record<string, string>;
    renderFooterActions?: (messages: UIMessage[]) => ReactNode;
    uiContext?: ProjectChatUiContext;
    onMarkAsSent?: (content: string) => void;
};

function ChatContent(props: ChatContentProps) {
    const [refreshVersion, setRefreshVersion] = React.useState(0);
    const [dismissedToolCallId, setDismissedToolCallId] = React.useState<string | null>(null);
    const [shouldScrollToLatestOnMount, setShouldScrollToLatestOnMount] = React.useState(false);

    return (
        <ChatContentSession
            key={`${props.chatId}-${refreshVersion}`}
            {...props}
            dismissedToolCallId={dismissedToolCallId}
            shouldScrollToLatestOnMount={shouldScrollToLatestOnMount}
            onDismissSuggestion={setDismissedToolCallId}
            onDidScrollToLatest={() => setShouldScrollToLatestOnMount(false)}
            onRefresh={(scrollToLatest = false) => {
                setShouldScrollToLatestOnMount(scrollToLatest);
                setRefreshVersion((value) => value + 1);
            }}
        />
    );
}

function ChatContentSession(
    props: ChatContentProps & {
        dismissedToolCallId: string | null;
        shouldScrollToLatestOnMount: boolean;
        onDismissSuggestion: (toolCallId: string | null) => void;
        onDidScrollToLatest: () => void;
        onRefresh: (scrollToLatest?: boolean) => void;
    }
) {
    const { initialMessage, onDidScrollToLatest, shouldScrollToLatestOnMount } = props;
    const { project } = useProject();
    const queryClient = useQueryClient();
    const { refetch: refetchChats } = useListProjectChatsQuery();
    const flags = useFlags(["google-drive-blind-compare"]);
    const compareQaOverride =
        config.env !== "prod" && new URLSearchParams(window.location.search).get("googleDriveCompare") === "1";
    const compareFlagEnabled = compareQaOverride || (flags["google-drive-blind-compare"]?.enabled ?? false);

    const {
        messages,
        status,
        sendMessage,
        appendAssistantMessage,
        error: sdkError,
        reload,
    } = useSdkChatWrapper(props.chatId, props.credentialsHeaders, {
        onMessageReceived: (message) => {
            refetchChats();

            for (const part of message.parts ?? []) {
                if (part.type !== "tool-invocation") continue;
                if (part.toolInvocation.state !== "result") continue;

                const toolName = part.toolInvocation.toolName as ChatToolName;

                if (TOOLS_INVALIDATING_TASKS.includes(toolName)) {
                    void queryClient.invalidateQueries({ queryKey: ["project", project.id, "tasks"] });
                }
                if (TOOLS_INVALIDATING_PROJECT.includes(toolName)) {
                    void queryClient.invalidateQueries({ queryKey: ["project", project.id] });
                }
            }
        },
    });

    const { displayError, isError, handleRetry } = useChatError(sdkError, reload);
    const isStreaming = status === "streaming";
    const isPending = status !== "ready" && !isError;

    const scrollContainerRef = React.useRef<HTMLDivElement>(null);
    const compareContainerRef = React.useRef<HTMLDivElement>(null);
    useScrollToBottomWhileStreaming(scrollContainerRef, isStreaming);

    const scrollToBottom = React.useCallback(() => {
        setTimeout(() => {
            scrollContainerRef.current?.scrollTo({ top: scrollContainerRef.current.scrollHeight, behavior: "smooth" });
        }, 100);
    }, []);

    const handleSend: ChatInputBoxOnSend = React.useCallback(
        (msg, uploads) => {
            scrollToBottom();
            return sendMessage(msg, mapUploadsToAttachments(uploads));
        },
        [scrollToBottom, sendMessage]
    );

    const handleSendTextOnly = React.useCallback(
        (msg: string) => {
            scrollToBottom();
            return sendMessage(msg, []);
        },
        [scrollToBottom, sendMessage]
    );

    useEffect(() => {
        if (initialMessage && !initialMessage.sent) {
            void handleSend(initialMessage.content, initialMessage.uploads);
            initialMessage.sent = true;
        }
    }, [handleSend, initialMessage, props.chatId]);

    useEffect(() => {
        scrollToBottom();
    }, [props.chatId, scrollToBottom]);

    useEffect(() => {
        if (!shouldScrollToLatestOnMount || messages.length === 0) {
            return;
        }

        scrollToBottom();
        onDidScrollToLatest();
    }, [messages.length, onDidScrollToLatest, shouldScrollToLatestOnMount, scrollToBottom]);

    const [compareState, setCompareState] = React.useState<CompareState>(null);
    const [compareError, setCompareError] = React.useState<string | undefined>(undefined);

    const createCompareMutation = useCreateGoogleDriveCompareMutation();
    const selectCompareMutation = useSelectGoogleDriveCompareMutation();

    useEffect(() => {
        if (!compareState) {
            return;
        }

        setTimeout(() => {
            compareContainerRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 50);
    }, [compareState]);

    const latestSuggestions = React.useMemo(() => {
        for (let i = messages.length - 1; i >= 0; i--) {
            for (const part of messages[i].parts) {
                if (
                    part.type === "tool-invocation" &&
                    part.toolInvocation.toolName === "getGoogleDriveContext" &&
                    part.toolInvocation.state === "result" &&
                    part.toolInvocation.result?.type === "file_suggestions"
                ) {
                    return {
                        toolCallId: part.toolInvocation.toolCallId,
                        files: part.toolInvocation.result.files as FileSuggestion[],
                    };
                }
            }
        }
        return null;
    }, [messages]);

    const showPanel =
        !!latestSuggestions &&
        latestSuggestions.toolCallId !== props.dismissedToolCallId &&
        (!compareState || compareState.status !== "generating");

    const isCompareActive = compareState !== null || selectCompareMutation.isPending;
    const isInputDisabled = isCompareActive;
    const contentWidthClass = isCompareActive ? "max-w-[96rem]" : "max-w-4xl";

    const handleStartCompare = async (sourceToolCallId: string, curatedExternalIds: string[]) => {
        if (!latestSuggestions) return;

        props.onDismissSuggestion(sourceToolCallId);
        setCompareError(undefined);
        setCompareState({ status: "generating" });

        try {
            const result = await createCompareMutation.mutateAsync({
                chatId: props.chatId,
                sourceToolCallId,
                curatedExternalIds,
            });

            setCompareState({
                status: "ready",
                experimentId: result.experimentId,
                optionA: result.optionA.text,
                optionB: result.optionB.text,
            });
        } catch {
            setCompareState(null);
            setCompareError("Could not create the draft comparison. Please try again.");
            props.onDismissSuggestion(null);
        }
    };

    const handleSelectWinner = async (selectedOption: "A" | "B", reason?: string) => {
        if (!compareState || compareState.status !== "ready") return;

        setCompareError(undefined);

        try {
            const result = await selectCompareMutation.mutateAsync({
                chatId: props.chatId,
                experimentId: compareState.experimentId,
                selectedOption,
                reason,
            });

            setCompareState(null);
            // Append the winning message directly to SDK state (backend already added to MongoDB)
            await appendAssistantMessage(result.winningText);
            scrollToBottom();
        } catch {
            setCompareError("Could not keep the selected draft. Please try again.");
        }
    };

    return (
        <ChatActionsContext.Provider value={{ sendMessage: handleSendTextOnly, onMarkAsSent: props.onMarkAsSent }}>
            <div className="flex w-full grow flex-row">
                <div
                    ref={scrollContainerRef}
                    className="flex min-w-0 flex-1 flex-row justify-center overflow-y-scroll p-4"
                >
                    <div
                        className={cn(
                            "relative h-full w-full flex-col items-center justify-center gap-6 transition-all duration-700",
                            contentWidthClass
                        )}
                    >
                        <GoogleDriveChatBanner />
                        <MessagesThread messages={messages} isPending={isPending} uiContext={props.uiContext} />
                        {displayError && <ErrorBox error={displayError} onRetry={handleRetry} className="mb-4" />}
                        {compareError && <ErrorBox error={compareError} className="mb-4" />}

                        {compareState?.status === "generating" && (
                            <div ref={compareContainerRef} className="my-6">
                                <GoogleDriveCompareStatusBlock
                                    title="Generating two options"
                                    description="Preparing two drafts so you can choose the one that works best."
                                />
                            </div>
                        )}

                        {compareState?.status === "ready" && (
                            <div ref={compareContainerRef}>
                                <GoogleDriveCompareBlock
                                    optionA={compareState.optionA}
                                    optionB={compareState.optionB}
                                    onSelect={handleSelectWinner}
                                    isLoading={selectCompareMutation.isPending}
                                />
                            </div>
                        )}

                        {!isCompareActive && (
                            <ChatInputBox
                                className={
                                    "sticky bottom-4 left-4 right-4 duration-500 animate-in fade-in-55 slide-in-from-bottom-1.5"
                                }
                                onSend={handleSend}
                                disabled={isInputDisabled}
                            />
                        )}
                        {props.renderFooterActions?.(messages)}
                    </div>
                </div>
                {showPanel && latestSuggestions && (
                    <div className="w-80 shrink-0 border-l">
                        <SuggestedContextPanel
                            key={latestSuggestions.toolCallId}
                            files={latestSuggestions.files}
                            toolCallId={latestSuggestions.toolCallId}
                            compareEnabled={compareFlagEnabled}
                            disabled={isPending}
                            onDismiss={() => props.onDismissSuggestion(latestSuggestions.toolCallId)}
                            onStartCompare={handleStartCompare}
                            comparePending={compareState?.status === "generating"}
                        />
                    </div>
                )}
            </div>
        </ChatActionsContext.Provider>
    );
}

function EmptyChatHeader({
    visible,
    uiContext,
    agentId,
    onClearAgent,
}: {
    visible: boolean;
    uiContext: ProjectChatUiContext;
    agentId?: string | null;
    onClearAgent?: () => void;
}) {
    const user = useCurrentOrgUser();
    const { project } = useProject();
    const { data: agents = [] } = useListAgentsQuery();
    const selectedAgent = agentId ? agents.find((a) => a.id === agentId) : null;
    const AgentIcon = selectedAgent ? getIconComponent(selectedAgent.icon) : null;

    return (
        <AnimateHeight height={visible ? "auto" : 0}>
            <div className={"flex flex-col items-center"}>
                <div className={"mb-12 flex flex-col items-center gap-2"}>
                    <div
                        className={
                            "flex flex-row items-center gap-1 self-center rounded-full border bg-muted/20 px-3 py-0.5 text-sm opacity-85 duration-300 animate-in zoom-in-95"
                        }
                    >
                        <span className={"font-medium text-muted-foreground"}>Project:</span>
                        <span className={"line-clamp-1 max-w-xs"}>{project.name}</span>
                    </div>
                    {!!uiContext && (
                        <div className={"flex flex-row items-center gap-1 text-sm text-muted-foreground"}>
                            <FocusIcon className={"h-4 w-4"} />
                            <UiContextReference uiContext={uiContext} />
                        </div>
                    )}
                </div>
                <div className={"flex w-full flex-col items-center"}>
                    {selectedAgent && <AgentBadge selectedAgent={selectedAgent} onClearAgent={onClearAgent} />}
                    <div className={"flex flex-row items-center justify-center gap-4 p-4 text-xl"}>
                        {AgentIcon ? (
                            <AgentIcon
                                className={
                                    "h-8 w-8 drop-shadow-glow-primary duration-1000 animate-in zoom-in-95 slide-in-from-right-2"
                                }
                            />
                        ) : (
                            <TekkrIcon
                                className={
                                    "drop-shadow-glow-primary duration-1000 animate-in zoom-in-95 slide-in-from-right-2"
                                }
                            />
                        )}
                        <div
                            className={
                                "zoom-in-99 flex flex-row items-center duration-700 animate-in slide-in-from-left-3"
                            }
                        >
                            <div className={"duration-1000 animate-in zoom-in-110"}>Hey {user.name.split(" ")[0]},</div>
                            &nbsp;<span className={"opacity-65"}>how can I help you?</span>
                        </div>
                    </div>
                </div>
            </div>
        </AnimateHeight>
    );
}

function AgentBadge({
    selectedAgent,
    onClearAgent,
}: {
    selectedAgent: { id: string; name: string; description?: string };
    onClearAgent?: () => void;
}) {
    return (
        <TooltipProvider>
            <Tooltip>
                <TooltipTrigger asChild>
                    <div
                        className={
                            "group flex cursor-default flex-row items-center gap-1.5 self-center rounded-full border border-primary/50 bg-primary/10 px-3 py-0.5 text-sm duration-300 animate-in zoom-in-95 hover:border-primary/70 hover:bg-primary/15"
                        }
                    >
                        <span className={"font-medium"}>{selectedAgent.name}</span>
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                onClearAgent?.();
                            }}
                            className={
                                "ml-1 cursor-pointer rounded-full p-0.5 hover:bg-primary/20 group-hover:opacity-100"
                            }
                            aria-label="Clear agent selection"
                        >
                            <XIcon className={"h-3 w-3"} />
                        </button>
                    </div>
                </TooltipTrigger>
                {selectedAgent.description && (
                    <TooltipContent>
                        <p className="max-w-xs">{selectedAgent.description}</p>
                    </TooltipContent>
                )}
            </Tooltip>
        </TooltipProvider>
    );
}

function UiContextReference({ uiContext }: { uiContext: ProjectChatUiContext }) {
    const { project } = useProject();
    if (!uiContext) {
        return null;
    } else if (uiContext.type === "workstream") {
        const ws = project.outline.workstreams.find((w) => w.id === uiContext.id)!;
        return (
            <>
                Workstream: <i>{ws.name}</i>
            </>
        );
    } else if (uiContext.type === "deliverable") {
        const ws = project.outline.workstreams.find((w) => w.id === uiContext.workstreamId)!;
        const deliverable = ws.deliverables.find((d) => d.id === uiContext.id)!;
        return (
            <>
                Deliverable: <i>{deliverable.name}</i>
            </>
        );
    } else if (uiContext.type === "meetings") {
        return <>Meetings</>;
    } else if (uiContext.type === "project-history") {
        return <>Project History</>;
    } else if (uiContext.type === "status-update") {
        return <>Status Update</>;
    }

    throw new Error("Unknown uiContext: " + (uiContext as ProjectChatUiContext)?.type);
}
